import { useCallback, useEffect, useRef, useState } from "react";

import { neoConversationController } from "../../../AppController";
import { useAppSelector } from "../../../hooks/redux";
import type {
  NeoConversationContentPart,
  NeoConversationInputContentPart,
  NeoConversationRichContentPart,
  NeoConversationStreamFrame,
} from "../../../hexagon/core/ports/driven/neoConversationRepository";
import { getAppVersion } from "../../../pages/Neo/apiBase";
import { neoArtifactsFromToolOutput } from "../../../pages/Neo/artifacts";
import { trackNeoEvent } from "../../../pages/Neo/neoAnalytics";
import { isNameMaskingActive } from "../../../util/common";
import {
  COPILOT_CONVERSATION_CREATED_EVENT,
  COPILOT_CONVERSATION_RENAMED_EVENT,
  COPILOT_CONVERSATION_VIEWED_EVENT,
  type CopilotConversationRenamedDetail,
} from "../../copilotEvents";
import type { FlagPatientPanel } from "../../neo/flagChatApi";
import { mapConversationHistory, toolStatus } from "./conversationHistory";
import { copilotStreams, type CopilotStream } from "./copilotStreams";
import {
  mergeRichContent,
  richContentFromParts,
  textFromContentParts,
} from "./richContent";
import {
  CONNECTION_INTERRUPTED,
  noticeForHttpError,
  noticeForStreamError,
} from "./turnNotices";
import type {
  CopilotAttachment,
  CopilotMessage,
  CopilotToolCall,
  CopilotToolStatus,
} from "./types";

const localId = (prefix: string): string =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const clientMessageId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : localId("client");

/** Long enough to read the ask in the header; the column holds 255. */
const TITLE_MAX_CHARS = 120;

const titleFromText = (text: string): string => {
  const clean = text.replace(/\s+/g, " ").trim();
  return clean.length > TITLE_MAX_CHARS
    ? `${clean.slice(0, TITLE_MAX_CHARS).trimEnd()}…`
    : clean;
};

/** Rejoining a turn the server is still running: poll this often, this long. */
const INFLIGHT_POLL_MS = 4_000;
const INFLIGHT_POLL_MAX_ATTEMPTS = 225; // ~15 minutes

const isUnfinished = (status: CopilotToolStatus) =>
  status === "pending" || status === "running";

/** Close out a reply: no live reasoning, no tool left spinning. */
const settleAssistant = (
  message: CopilotMessage,
  unfinishedTools: CopilotToolStatus
): CopilotMessage => ({
  ...message,
  reasoning: message.reasoning
    ? { ...message.reasoning, active: false }
    : undefined,
  tools: message.tools?.map((tool) =>
    isUnfinished(tool.status) ? { ...tool, status: unfinishedTools } : tool
  ),
});

const uiContextPart = (
  clinicId: number | undefined,
  extra?: Record<string, unknown>
): NeoConversationInputContentPart => {
  const route = window.location.hash || window.location.pathname;
  const patientId = Number(route.match(/\/patient\/(\d+)(?:\/|$)/)?.[1]);
  let timezone: string | undefined;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    // Some minimal browsers throw without a locale; skip the hint.
  }
  const appVersion = getAppVersion();
  return {
    type: "neo_ui_context",
    selected_clinic_id: clinicId,
    route,
    viewport:
      window.innerWidth <= 640
        ? "mobile"
        : window.innerWidth <= 1024
        ? "tablet"
        : "web",
    ...(Number.isInteger(patientId) && patientId > 0
      ? { selected_patient_id: patientId }
      : {}),
    ...(timezone ? { app_timezone: timezone } : {}),
    ...(appVersion ? { app_version: appVersion } : {}),
    ...(extra ? { extra } : {}),
  };
};

/** Context the model needs but the user shouldn't see in their bubble
 *  (e.g. which workflow output a chat is about). */
export interface CopilotTurnContext {
  /** Appended to the first message as its own text part. */
  text: string;
  /** Sent as ``neo_ui_context.extra`` on that message. */
  extra?: Record<string, unknown>;
}

interface SendOptions {
  /** Already-uploaded files to attach again (regenerate / retry). */
  attachments?: CopilotAttachment[];
  /** Reuse a turn's id so the backend resumes it instead of starting over. */
  clientMessageId?: string;
  /** Resuming: replace the reply group starting at this message id instead
   *  of adding a new user message. */
  resumeFrom?: string;
}

export const useCopilotConversation = ({
  sessionKey = "default",
  conversationId,
  initialClinicId,
  initialFiles,
  initialPrompt,
  onInitialPromptConsumed,
  onConversationCreated,
  turnContext,
}: {
  /** Sent with the first message of this session only. */
  turnContext?: CopilotTurnContext | null;
  sessionKey?: string;
  conversationId?: number | null;
  initialClinicId?: number;
  initialFiles?: File[];
  initialPrompt?: string | null;
  onInitialPromptConsumed?: () => void;
  /** A new chat got its backend id; the page moves it into the URL. */
  onConversationCreated?: (conversationId: number) => void;
}) => {
  const selectedClinicId = useAppSelector(
    (state) => state.clinic.userClinic?.id
  );
  const clinicId = selectedClinicId ?? initialClinicId;
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [title, setTitle] = useState("New Conversation");
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [inflightAssistantId, setInflightAssistantId] = useState<number | null>(
    null
  );
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  // Flag-launched chats: the patient snapshot shown beside the conversation.
  const [patientPanel, setPatientPanel] = useState<FlagPatientPanel | null>(
    null
  );
  const [conversationReady, setConversationReady] = useState(false);
  // The conversation on screen, and whether it has a live turn in
  // ``copilotStreams`` (possibly started before the user last left it).
  const [viewedConversationId, setViewedConversationId] = useState<
    number | null
  >(null);
  const [streamLive, setStreamLive] = useState(false);
  const conversationIdRef = useRef<number | null>(null);
  // Set when this session created the conversation, so the URL catching up
  // to it (``?conversation=<id>``) doesn't reset the live stream.
  const adoptedIdRef = useRef<number | null>(null);
  const conversationIdPropRef = useRef(conversationId);
  conversationIdPropRef.current = conversationId;
  // Bumped on every real session change; async work started under an older
  // session drops its results instead of writing into the new conversation.
  const sessionRef = useRef(0);
  const titleRef = useRef("New Conversation");
  const customTitleRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const initialSentRef = useRef(false);
  const turnContextRef = useRef(turnContext);
  turnContextRef.current = turnContext;
  const turnContextSentRef = useRef(false);
  const busyRef = useRef(false);
  const loadingHistoryRef = useRef(false);
  const inflightRef = useRef<number | null>(null);
  const stoppedClientIdsRef = useRef(new Set<string>());
  const messagesRef = useRef<CopilotMessage[]>([]);
  messagesRef.current = messages;

  const setInflight = useCallback((id: number | null) => {
    inflightRef.current = id;
    setInflightAssistantId(id);
  }, []);

  const markViewedThrough = useCallback(
    async (targetConversationId: number, sequenceValue: unknown) => {
      const sequenceNum = Number(sequenceValue);
      if (!Number.isInteger(sequenceNum) || sequenceNum <= 0) return;
      const result = await neoConversationController.markConversationViewed(
        targetConversationId,
        sequenceNum
      );
      if (result.success) {
        window.dispatchEvent(
          new CustomEvent(COPILOT_CONVERSATION_VIEWED_EVENT)
        );
      }
    },
    []
  );

  useEffect(() => {
    titleRef.current = title;
  }, [title]);

  const send = useCallback(
    async (
      rawPrompt?: string,
      files: File[] = [],
      options: SendOptions = {}
    ): Promise<boolean> => {
      const prompt = (rawPrompt ?? input).trim();
      const reused = (options.attachments ?? []).filter(
        (attachment) => attachment.id != null
      );
      if (
        (!prompt && files.length === 0 && reused.length === 0) ||
        busyRef.current ||
        copilotStreams.isStreaming(conversationIdRef.current) ||
        inflightRef.current != null ||
        loadingHistoryRef.current
      ) {
        return false;
      }

      const session = sessionRef.current;
      const isCurrent = () => session === sessionRef.current;
      // Created up front so Stop works during create/upload too.
      const abortController = new AbortController();
      abortRef.current = abortController;
      busyRef.current = true;
      setIsStreaming(true);
      const release = () => {
        if (!isCurrent()) return;
        if (abortRef.current === abortController) abortRef.current = null;
        busyRef.current = false;
        setIsStreaming(false);
      };
      const failBeforeTurn = (error: string) => {
        if (isCurrent()) {
          setMessages((current) => [
            ...current,
            {
              id: localId("error"),
              role: "assistant",
              content: "",
              error,
              retryable: false,
            },
          ]);
        }
        release();
        return false;
      };

      let targetId = conversationIdRef.current;
      if (targetId == null) {
        const conversationTitle = customTitleRef.current
          ? titleRef.current
          : titleFromText(prompt) ||
            titleFromText(files[0]?.name ?? "") ||
            "New Conversation";
        const created = await neoConversationController.createConversation({
          title: conversationTitle,
          project_id: null,
          workflow_id: null,
          scope_mode: "unscoped",
          first_client: "cip-web",
        });
        if (!isCurrent()) return false;
        if (!created.success || typeof created.data?.id !== "number") {
          return failBeforeTurn(
            "Couldn’t start the conversation. Please try again."
          );
        }
        targetId = created.data.id;
        conversationIdRef.current = targetId;
        adoptedIdRef.current = targetId;
        setViewedConversationId(targetId);
        setConversationReady(true);
        if (customTitleRef.current && titleRef.current !== conversationTitle) {
          // Renamed while the create was in flight — keep the user's title.
          void neoConversationController.updateConversationTitle(
            targetId,
            titleRef.current
          );
        } else {
          const createdTitle = created.data.title?.trim() || conversationTitle;
          titleRef.current = createdTitle;
          setTitle(createdTitle);
        }
        window.dispatchEvent(
          new CustomEvent(COPILOT_CONVERSATION_CREATED_EVENT)
        );
        onConversationCreated?.(targetId);
      }
      if (abortController.signal.aborted) {
        release();
        return false;
      }
      const turnConversationId = targetId;

      const uploadResults = await Promise.all(
        files.map((file) =>
          neoConversationController.uploadFile(turnConversationId, file)
        )
      );
      if (!isCurrent()) return false;
      if (abortController.signal.aborted) {
        release();
        return false;
      }
      if (uploadResults.some((result) => !result.success || !result.data)) {
        return failBeforeTurn(
          "Couldn’t attach one of the files. Please try again."
        );
      }
      const attachments: CopilotAttachment[] = [
        ...uploadResults.flatMap((result) =>
          result.data
            ? [
                {
                  id: result.data.id,
                  name: result.data.filename,
                  mimeType: result.data.mime_type,
                },
              ]
            : []
        ),
        ...reused,
      ];
      const turnClientId = options.clientMessageId ?? clientMessageId();
      const assistantId = localId("assistant");
      const assistantMessage: CopilotMessage = {
        id: assistantId,
        role: "assistant",
        content: "",
        reasoning: { content: "", active: true },
        tools: [],
      };
      // From here the turn lives in ``copilotStreams``: it keeps streaming
      // (the backend cancels on disconnect) even if the user opens another
      // chat, and whichever view shows this conversation follows it.
      const stream: CopilotStream = {
        messages: [...messagesRef.current],
        title: titleRef.current,
        abort: abortController,
        assistantDbId: null,
        clientMessageId: turnClientId,
      };
      copilotStreams.begin(turnConversationId, stream);
      const write = (
        update: (messages: CopilotMessage[]) => CopilotMessage[]
      ) => copilotStreams.update(turnConversationId, update);
      if (abortRef.current === abortController) abortRef.current = null;
      busyRef.current = false;
      setIsStreaming(false);
      if (options.resumeFrom) {
        const resumeFrom = options.resumeFrom;
        write((current) => {
          const index = current.findIndex(
            (message) => message.id === resumeFrom
          );
          return [
            ...(index >= 0 ? current.slice(0, index) : current),
            assistantMessage,
          ];
        });
      } else {
        write((current) => [
          ...current,
          {
            id: localId("user"),
            role: "user",
            content: prompt,
            attachments,
            clientMessageId: turnClientId,
          },
          assistantMessage,
        ]);
      }
      // Only a composer send consumes the draft; regenerate and form submits
      // leave whatever the user is typing alone.
      if (rawPrompt === undefined) setInput("");
      const startedAt = Date.now();
      const context = turnContextSentRef.current
        ? null
        : turnContextRef.current ?? null;
      if (context) turnContextSentRef.current = true;
      const contentParts: NeoConversationInputContentPart[] = [
        ...(prompt ? [{ type: "text" as const, text: prompt }] : []),
        ...(context ? [{ type: "text" as const, text: context.text }] : []),
        ...attachments.map(
          (file): NeoConversationInputContentPart => ({
            type: file.mimeType?.startsWith("image/") ? "image" : "document",
            source: { type: "neo_file", neo_file_id: file.id as number },
            title: file.name,
          })
        ),
        uiContextPart(clinicId, context?.extra),
      ];

      // One local message per backend assistant round, like history; tool
      // results can land after the next round opens, so they search the
      // whole turn.
      const turnIds = new Set([assistantId]);
      let currentId = assistantId;
      let sawTerminal = false;
      const patch = (
        target: (message: CopilotMessage, all: CopilotMessage[]) => boolean,
        update: (message: CopilotMessage) => CopilotMessage
      ) =>
        write((current) =>
          current.map((message) =>
            target(message, current) ? update(message) : message
          )
        );
      const patchCurrent = (
        update: (message: CopilotMessage) => CopilotMessage
      ) => {
        const target = currentId;
        patch((message) => message.id === target, update);
      };
      const patchTurn = (update: (message: CopilotMessage) => CopilotMessage) =>
        patch((message) => turnIds.has(message.id), update);
      const patchTool = (
        matches: (tool: CopilotToolCall) => boolean,
        update: (message: CopilotMessage) => CopilotMessage
      ) => {
        const owner = currentId;
        patch((message, all) => {
          const holder =
            all.find(
              (candidate) =>
                turnIds.has(candidate.id) && candidate.tools?.some(matches)
            )?.id ?? owner;
          return message.id === holder;
        }, update);
      };

      const handleFrame = ({ event, data }: NeoConversationStreamFrame) => {
        const payload = data ?? {};

        if (event === "meta") {
          const assistant = payload.assistant_message as
            | { id?: unknown }
            | undefined;
          if (typeof assistant?.id === "number") {
            copilotStreams.setAssistantDbId(turnConversationId, assistant.id);
          }
          return;
        }

        if (event === "assistant_message_created") {
          const next = payload.assistant_message as
            | { id?: unknown }
            | undefined;
          if (typeof next?.id === "number") {
            copilotStreams.setAssistantDbId(turnConversationId, next.id);
          }
          const nextId = localId("assistant");
          const previousId = currentId;
          write((current) => [
            ...current.map((message) =>
              message.id === previousId && message.reasoning?.active
                ? {
                    ...message,
                    reasoning: { ...message.reasoning, active: false },
                  }
                : message
            ),
            {
              id: nextId,
              role: "assistant",
              content: "",
              reasoning: { content: "", active: true },
              tools: [],
            },
          ]);
          turnIds.add(nextId);
          currentId = nextId;
          return;
        }

        if (event === "thinking_start") {
          patchCurrent((message) => ({
            ...message,
            reasoning: {
              content: message.reasoning?.content ?? "",
              active: true,
            },
          }));
          return;
        }

        if (event === "thinking_delta") {
          const text = String(payload.text ?? "");
          if (!text) return;
          patchCurrent((message) => ({
            ...message,
            reasoning: {
              content: `${message.reasoning?.content ?? ""}${text}`,
              active: true,
            },
          }));
          return;
        }

        if (event === "thinking_stop") {
          const durationMs = Number(
            payload.duration_ms ?? Date.now() - startedAt
          );
          patchCurrent((message) => ({
            ...message,
            reasoning: {
              content: message.reasoning?.content ?? "",
              durationMs,
              active: false,
            },
          }));
          return;
        }

        if (event === "text_delta") {
          const text = String(payload.text ?? "");
          if (!text) return;
          patchCurrent((message) => ({
            ...message,
            content: message.content + text,
            reasoning: message.reasoning
              ? { ...message.reasoning, active: false }
              : undefined,
          }));
          return;
        }

        if (event === "tool_use_start") {
          const id = String(payload.tool_call_id ?? localId("tool"));
          const name = String(payload.tool_name ?? "Using a tool");
          patchCurrent((message) => ({
            ...message,
            tools: [
              ...(message.tools ?? []).filter((tool) => tool.id !== id),
              { id, name, status: "pending" },
            ],
          }));
          return;
        }

        if (event === "tool_calls_projected") {
          const projected = Array.isArray(payload.tool_calls)
            ? (payload.tool_calls as Array<Record<string, unknown>>)
            : [];
          patchCurrent((message) => {
            const existing = new Map(
              (message.tools ?? []).map((tool) => [tool.id, tool])
            );
            projected.forEach((tool) => {
              const id = String(
                tool.tool_call_id ?? tool.id ?? localId("tool")
              );
              existing.set(id, {
                ...existing.get(id),
                id,
                rowId: Number(tool.id),
                name: String(tool.tool_name ?? "Using a tool"),
                status: existing.get(id)?.status ?? "pending",
                input: tool.tool_input,
              });
            });
            return { ...message, tools: [...existing.values()] };
          });
          return;
        }

        if (event === "tool_call_running" || event === "tool_call_result") {
          const rowId = Number(payload.id);
          const resultOutput = payload.output ?? payload.tool_output;
          const toolCallId =
            typeof payload.tool_call_id === "string"
              ? payload.tool_call_id
              : undefined;
          const matches = (tool: CopilotToolCall) =>
            (Number.isFinite(rowId) && tool.rowId === rowId) ||
            (toolCallId != null && tool.id === toolCallId);
          const optimisticArtifacts =
            event === "tool_call_result"
              ? neoArtifactsFromToolOutput(resultOutput).map(
                  ({ part }) => part as NeoConversationRichContentPart
                )
              : [];
          patchTool(matches, (message) => {
            const tools = [...(message.tools ?? [])];
            const toolIndex = tools.findIndex(matches);
            const previous =
              toolIndex >= 0
                ? tools[toolIndex]
                : {
                    id: toolCallId ?? localId("tool"),
                    name: String(payload.tool_name ?? "Using a tool"),
                    status: "pending" as const,
                  };
            const updated: CopilotToolCall = {
              ...previous,
              rowId: Number.isFinite(rowId) ? rowId : previous.rowId,
              status:
                event === "tool_call_running"
                  ? "running"
                  : toolStatus(payload.status),
              input: payload.tool_input ?? previous.input,
              output: resultOutput ?? previous.output,
              durationMs:
                typeof payload.duration_ms === "number"
                  ? payload.duration_ms
                  : previous.durationMs,
              isError: Boolean(payload.is_error),
            };
            if (toolIndex >= 0) tools[toolIndex] = updated;
            else tools.push(updated);
            return {
              ...message,
              tools,
              richContent: mergeRichContent(
                message.richContent,
                optimisticArtifacts
              ),
            };
          });
          return;
        }

        if (event === "done") {
          sawTerminal = true;
          const assistant = payload.assistant_message as
            | Record<string, unknown>
            | undefined;
          const assistantParts = Array.isArray(assistant?.content_parts)
            ? (assistant.content_parts as NeoConversationContentPart[])
            : [];
          const canonicalText = textFromContentParts(assistantParts);
          const canonicalRichContent = richContentFromParts(assistantParts);
          patchCurrent((message) => ({
            ...message,
            content:
              typeof assistant?.content === "string" && assistant.content.trim()
                ? assistant.content
                : canonicalText
                ? canonicalText
                : message.content,
            richContent: mergeRichContent(
              message.richContent,
              canonicalRichContent
            ),
            truncated: assistant?.finish_reason === "length",
            reasoning: message.reasoning
              ? {
                  ...message.reasoning,
                  durationMs:
                    message.reasoning.durationMs ?? Date.now() - startedAt,
                }
              : undefined,
          }));
          patchTurn((message) => settleAssistant(message, "completed"));
          // Only mark it read if someone is looking; otherwise the sidebar
          // keeps the "finished, unviewed" dot.
          if (
            copilotStreams.isWatched(turnConversationId) ||
            (isCurrent() && conversationIdRef.current === turnConversationId)
          ) {
            void markViewedThrough(turnConversationId, assistant?.sequence_num);
          }
          return;
        }

        if (event === "error") {
          sawTerminal = true;
          const notice = noticeForStreamError(payload);
          patchCurrent((message) => ({ ...message, ...notice }));
          patchTurn((message) => settleAssistant(message, "cancelled"));
          trackNeoEvent("turn stream error", {
            conversation_id: turnConversationId,
            title: notice.errorTitle ?? null,
            retryable: notice.retryable ?? null,
          });
        }
      };

      trackNeoEvent("message sent", {
        conversation_id: turnConversationId,
        attachment_count: attachments.length,
        resumed: Boolean(options.resumeFrom),
      });
      const result = await neoConversationController.streamTurn(
        turnConversationId,
        {
          client_message_id: turnClientId,
          content: prompt,
          content_parts: contentParts,
          selected_clinic_id: clinicId ?? null,
          mask_pii: isNameMaskingActive() ? ["name"] : null,
        },
        handleFrame,
        abortController.signal
      );

      if (!abortController.signal.aborted) {
        if (!result.success) {
          const notice = noticeForHttpError(result.error);
          patchCurrent((message) => ({
            ...message,
            ...notice,
            reasoning: undefined,
          }));
          patchTurn((message) => settleAssistant(message, "cancelled"));
          trackNeoEvent("turn stream HTTP error", {
            conversation_id: turnConversationId,
            status: result.error?.status ?? null,
            code: result.error?.code ?? null,
          });
        } else if (!sawTerminal) {
          // The socket closed without ``done`` or ``error``.
          patchCurrent((message) => ({
            ...message,
            ...CONNECTION_INTERRUPTED,
          }));
          patchTurn((message) => settleAssistant(message, "cancelled"));
          trackNeoEvent("turn stream interrupted", {
            conversation_id: turnConversationId,
          });
        }
      }
      copilotStreams.end(turnConversationId, stream);
      return true;
    },
    [clinicId, input, markViewedThrough, onConversationCreated]
  );

  /** The user turn a reply group answered (the nearest user row above it). */
  const promptFor = useCallback(
    (assistantId: string): CopilotMessage | undefined => {
      const current = messagesRef.current;
      const index = current.findIndex((message) => message.id === assistantId);
      if (index <= 0) return undefined;
      return current
        .slice(0, index)
        .reverse()
        .find((message) => message.role === "user");
    },
    []
  );

  /** Re-ask the prompt behind ``assistantId`` as a fresh turn. */
  const regenerate = useCallback(
    (assistantId: string) => {
      const prompt = promptFor(assistantId);
      if (!prompt) return;
      trackNeoEvent("turn retried", {
        conversation_id: conversationIdRef.current,
        assistant_message_id: assistantId,
        resumed: false,
      });
      void send(prompt.content, [], { attachments: prompt.attachments });
    },
    [promptFor, send]
  );

  /** "Try again" on a failed reply: resume the same turn unless the user
   *  stopped it, in which case reusing the id would just replay the stop. */
  const retry = useCallback(
    (assistantId: string) => {
      const prompt = promptFor(assistantId);
      if (!prompt) return;
      const stoppedId = prompt.clientMessageId;
      const resume =
        stoppedId != null && !stoppedClientIdsRef.current.has(stoppedId);
      trackNeoEvent("turn retried", {
        conversation_id: conversationIdRef.current,
        assistant_message_id: assistantId,
        resumed: resume,
      });
      void send(prompt.content, [], {
        attachments: prompt.attachments,
        ...(resume
          ? { clientMessageId: stoppedId, resumeFrom: assistantId }
          : {}),
      });
    },
    [promptFor, send]
  );

  const stop = useCallback(() => {
    const stream = copilotStreams.get(conversationIdRef.current);
    const backendId = stream?.assistantDbId ?? inflightRef.current;
    const lastUser = [...messagesRef.current]
      .reverse()
      .find((message) => message.role === "user");
    const stoppedClientId =
      stream?.clientMessageId ?? lastUser?.clientMessageId;
    if (stoppedClientId) stoppedClientIdsRef.current.add(stoppedClientId);
    abortRef.current?.abort();
    abortRef.current = null;
    busyRef.current = false;
    setIsStreaming(false);
    setInflight(null);
    const settle = (current: CopilotMessage[]) => {
      const lastAssistant = [...current]
        .reverse()
        .find((message) => message.role === "assistant");
      return current.map((message) =>
        message.role !== "assistant"
          ? message
          : {
              ...settleAssistant(message, "cancelled"),
              stopped:
                message.id === lastAssistant?.id && !message.error
                  ? true
                  : message.stopped,
            }
      );
    };
    if (stream && conversationIdRef.current != null) {
      copilotStreams.update(conversationIdRef.current, settle);
      stream.abort.abort();
    } else {
      setMessages(settle);
    }
    trackNeoEvent("message stopped", {
      assistant_message_id: backendId ?? null,
    });
    // Aborting the fetch alone leaves the turn running (and billing) on the
    // server; tell it this was an explicit stop.
    if (backendId != null) {
      void neoConversationController.cancelMessage(backendId, "user_stop");
    }
  }, [setInflight]);

  const renameConversation = useCallback(
    async (rawTitle: string) => {
      const conversationId = conversationIdRef.current;
      const nextTitle = rawTitle.trim();
      if (!nextTitle) return false;
      if (conversationId == null) {
        customTitleRef.current = true;
        titleRef.current = nextTitle;
        setTitle(nextTitle);
        return true;
      }

      const previousTitle = title;
      setTitle(nextTitle);
      const result = await neoConversationController.updateConversationTitle(
        conversationId,
        nextTitle
      );
      if (!result.success) {
        setTitle(previousTitle);
        return false;
      }
      setTitle(result.data?.title?.trim() || nextTitle);
      window.dispatchEvent(new CustomEvent(COPILOT_CONVERSATION_CREATED_EVENT));
      return true;
    },
    [title]
  );

  useEffect(() => {
    const propId = conversationIdPropRef.current;
    // The chat this session just created moved into the URL: same
    // conversation, so keep the live stream and messages.
    if (propId != null && propId === adoptedIdRef.current) return;
    sessionRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    conversationIdRef.current = null;
    adoptedIdRef.current = null;
    busyRef.current = false;
    loadingHistoryRef.current = false;
    initialSentRef.current = false;
    turnContextSentRef.current = false;
    customTitleRef.current = false;
    const loadingExistingConversation = propId != null;
    titleRef.current = loadingExistingConversation ? "" : "New Conversation";
    setInflight(null);
    setViewedConversationId(null);
    setMessages([]);
    setTitle(loadingExistingConversation ? "" : "New Conversation");
    setInput("");
    setIsStreaming(false);
    setIsLoadingHistory(loadingExistingConversation);
    setConversationReady(false);
    setPatientPanel(null);
  }, [sessionKey, setInflight]);

  useEffect(() => {
    if (conversationId == null || conversationId === adoptedIdRef.current) {
      return;
    }
    let active = true;
    let pollTimer: number | undefined;
    const session = sessionRef.current;
    conversationIdRef.current = conversationId;
    setConversationReady(true);
    setViewedConversationId(conversationId);
    const live = copilotStreams.get(conversationId);
    // A turn that ended while we were away is in history now; drop the
    // stale snapshot so it can't overwrite what we load.
    if (!live) copilotStreams.takeFinished(conversationId);
    if (live) {
      // Coming back to a chat whose reply is still streaming in the
      // background: follow the live turn instead of reloading history.
      setTitle(live.title);
      setIsLoadingHistory(false);
      return;
    }
    loadingHistoryRef.current = true;
    setIsLoadingHistory(true);

    const load = async (attempt: number) => {
      const result = await neoConversationController.getConversationHistory(
        conversationId
      );
      if (!active || session !== sessionRef.current) return;
      const initial = attempt === 0;
      // Stop pressed while rejoining a running turn: stop polling.
      if (!initial && inflightRef.current == null) return;
      if (initial) {
        loadingHistoryRef.current = false;
        setIsLoadingHistory(false);
      }
      if (!result.success || !result.data) {
        if (initial) {
          setMessages([
            {
              id: localId("history-error"),
              role: "assistant",
              content: "",
              error: "Couldn’t load this conversation.",
              retryable: false,
            },
          ]);
        }
        setInflight(null);
        return;
      }
      const mapped = mapConversationHistory(result.data);
      if (initial) {
        setTitle(mapped.title);
        const panel = result.data.conversation?.extra_info?.patient_panel;
        setPatientPanel(
          panel && typeof panel === "object"
            ? (panel as unknown as FlagPatientPanel)
            : null
        );
      }
      setMessages(mapped.messages);
      const stillRunning =
        mapped.inflightAssistantId != null &&
        attempt < INFLIGHT_POLL_MAX_ATTEMPTS;
      setInflight(stillRunning ? mapped.inflightAssistantId : null);
      if (stillRunning) {
        // Refreshed (or came back) mid-turn: follow it until it settles.
        pollTimer = window.setTimeout(
          () => void load(attempt + 1),
          INFLIGHT_POLL_MS
        );
        return;
      }
      void markViewedThrough(conversationId, mapped.latestSequenceNum);
    };
    void load(0);
    return () => {
      active = false;
      window.clearTimeout(pollTimer);
    };
  }, [conversationId, markViewedThrough, setInflight]);

  useEffect(() => {
    if (viewedConversationId == null) {
      setStreamLive(false);
      return;
    }
    const id = viewedConversationId;
    const sync = () => {
      const stream = copilotStreams.get(id);
      setStreamLive(Boolean(stream));
      const latest = stream?.messages ?? copilotStreams.takeFinished(id);
      if (latest) setMessages(latest);
    };
    const unsubscribe = copilotStreams.subscribe(id, sync);
    sync();
    return unsubscribe;
  }, [viewedConversationId]);

  useEffect(() => {
    const prompt = initialPrompt?.trim();
    if (
      clinicId == null ||
      (!prompt && (initialFiles?.length ?? 0) === 0) ||
      initialSentRef.current
    ) {
      return;
    }
    // Opening an existing chat with a question (e.g. "ask about this flag"):
    // wait until its history is on screen and no turn is running, so the
    // question lands after the seeded messages instead of being dropped.
    if (
      conversationId != null &&
      (isLoadingHistory ||
        loadingHistoryRef.current ||
        conversationIdRef.current !== conversationId ||
        inflightAssistantId != null ||
        streamLive)
    ) {
      return;
    }
    initialSentRef.current = true;
    onInitialPromptConsumed?.();
    void send(prompt, initialFiles);
  }, [
    clinicId,
    conversationId,
    inflightAssistantId,
    initialFiles,
    initialPrompt,
    isLoadingHistory,
    onInitialPromptConsumed,
    send,
    streamLive,
  ]);

  useEffect(() => {
    const onRenamed = (event: Event) => {
      const { id, title: renamed } = (
        event as CustomEvent<CopilotConversationRenamedDetail>
      ).detail;
      if (id === conversationIdRef.current) setTitle(renamed);
    };
    window.addEventListener(COPILOT_CONVERSATION_RENAMED_EVENT, onRenamed);
    return () =>
      window.removeEventListener(COPILOT_CONVERSATION_RENAMED_EVENT, onRenamed);
  }, []);

  // Only a turn still being set up (create/upload) dies with the page; a
  // streaming turn carries on in ``copilotStreams``.
  useEffect(() => () => abortRef.current?.abort(), []);

  return {
    messages,
    title,
    input,
    setInput,
    send,
    regenerate,
    retry,
    stop,
    renameConversation,
    conversationReady,
    // A turn rejoined after refresh counts as streaming: composer locked,
    // Stop available.
    isStreaming: isStreaming || streamLive || inflightAssistantId != null,
    isLoadingHistory,
    patientPanel,
  };
};
